<template>
  <div class="artifact-version-list">
    <div class="info-group">
      <div class="info-group__title">
        <span>制品版本列表</span>
        <span class="extend">共{{ pagination.total }}个版本</span>
      </div>

      <!-- 搜索过滤区域 -->
      <div class="search-section">
        <a-form-model :form="filterForm" :model="filterForm" class="search-form">
          <a-form-model-item>
            <a-input v-model="filterForm.keyword" placeholder="请输入版本号或Commit ID进行搜索" class="search-input" @change="onSearch" @pressEnter="onSearch">
              <a-icon slot="prefix" type="search" />
            </a-input>
          </a-form-model-item>
        </a-form-model>
      </div>

      <!-- 版本列表 -->
      <div class="version-list">
        <a-spin size="large" :spinning="loading">
          <div v-for="version in tableData" :key="version.id" class="version-item" :class="{ selected: selectedVersionId === version.id }" @click="handleItemClick(version)">
            <div class="version-main">
              <div class="version-info">
                <div class="version-number">
                  <a-tag color="blue" size="large">{{ version.version }}</a-tag>
                  <a-tag v-if="version.git_tag_name" color="green" size="small">
                    {{ version.git_tag_name }}
                  </a-tag>
                </div>
                <div class="version-meta">
                  <span class="creator">
                    <a-avatar :size="16" :src="version.creator?.avatar_url" />
                    <span class="creator-name">{{ version.creator?.nickname || '--' }}</span>
                  </span>
                  <span class="update-time">
                    <a-icon type="clock-circle" />
                    {{ formatTime(version.created_time) }}
                  </span>
                </div>
              </div>
              <div class="version-arrow">
                <a-icon type="right" />
              </div>
            </div>
          </div>
        </a-spin>

        <!-- 空状态 -->
        <div v-if="!loading && tableData.length === 0" class="empty-state">
          <a-empty description="暂无版本数据" />
        </div>
      </div>

      <!-- 分页 -->
      <div class="pagination-wrapper">
        <a-pagination v-model="pagination.current" size="small" :total="pagination.total" :page-size="pagination.pageSize" @change="handlePageChange" />
      </div>
    </div>
  </div>
</template>

<script>
import { artifactVersionList } from '@/api/system/artifact';

export default {
  name: 'ArtifactVersionList',
  props: {
    artifactId: {
      type: [String, Number],
      required: true
    },
    selectedVersionId: {
      type: [String, Number],
      default: null
    }
  },
  data() {
    return {
      loading: false,
      filterForm: {
        service_id: null,
        artifact_id: this.artifactId,
        keyword: null,
        page_number: 1,
        page_size: 10
      },
      pagination: {
        total: 0,
        current: 1,
        pageSize: 10
      },
      tableData: []
    };
  },
  watch: {
    artifactId: {
      handler(newVal) {
        this.filterForm.artifact_id = newVal;
        this.getArtifactVersionList();
      },
      immediate: true
    }
  },
  methods: {
    formatTime(time) {
      if (!time) return '--';
      const date = new Date(time);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day} ${hours}:${minutes}`;
    },

    async getArtifactVersionList() {
      this.loading = true;
      try {
        const res = await artifactVersionList(this.filterForm);
        this.tableData = res.data.items;
        this.pagination.total = res.data.total;

        // 如果没有选中版本且有数据，默认选中第一个
        if (!this.selectedVersionId && this.tableData.length > 0) {
          this.$emit('select-version', this.tableData[0].id);
        }
      } finally {
        this.loading = false;
      }
    },

    handlePageChange(page, pageSize) {
      this.pagination.current = page;
      this.filterForm.page_number = page;
      this.filterForm.page_size = pageSize;
      this.getArtifactVersionList();
    },

    onSearch() {
      this.pagination.current = 1;
      this.filterForm.page_number = 1;
      this.getArtifactVersionList();
    },

    handleItemClick(version) {
      this.$emit('select-version', version.id);
    }
  }
};
</script>

<style lang="less" scoped>
.artifact-version-list {
  height: 100%;
  display: flex;
  flex-direction: column;

  .info-group {
    padding: 16px;
    margin-bottom: 16px;
    height: 100%;
    display: flex;
    flex-direction: column;

    &:last-child {
      margin-bottom: 0;
    }

    &__title {
      margin-bottom: 12px;
      color: rgba(0, 0, 0, 0.85);
      font-size: 14px;
      font-weight: 500;
      position: relative;
      padding-left: 12px;
      display: flex;

      &::before {
        content: '';
        position: absolute;
        left: 0;
        top: 50%;
        transform: translateY(-50%);
        width: 4px;
        height: 16px;
        background-color: #1890ff;
        border-radius: 2px;
      }

      .extend {
        margin-left: auto;
        font-weight: normal;
        color: rgba(0, 0, 0, 0.45);
      }
    }
  }

  .search-section {
    padding-bottom: 8px;

    .search-form {
      .ant-form-item {
        margin-bottom: 0;
      }

      .search-input {
        .ant-input {
          border-radius: 6px;
        }
      }
    }
  }

  .version-list {
    flex: 1;
    overflow-y: auto;
    text-align: center;
  }

  .version-item {
    padding: 8px;
    border-radius: 8px;
    border-bottom: 1px solid #f5f5f5;
    cursor: pointer;
    margin: 2px 0;
    transition: all 0.3s ease;

    &:hover {
      background-color: #f8f9fa;
    }

    &.selected {
      background-color: #e6f7ff;
      border-left: 3px solid #1890ff;
    }

    .version-main {
      display: flex;
      justify-content: space-between;
      align-items: center;

      .version-info {
        flex: 1;

        .version-number {
          margin-bottom: 8px;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .version-meta {
          display: flex;
          align-items: center;
          gap: 16px;
          font-size: 12px;
          color: #666;

          .creator {
            display: flex;
            align-items: center;
            gap: 4px;

            .creator-name {
              font-size: 12px;
            }
          }

          .update-time {
            display: flex;
            align-items: center;
            gap: 4px;

            .anticon {
              font-size: 12px;
            }
          }
        }
      }

      .version-arrow {
        color: #ccc;
        font-size: 12px;
      }
    }
  }

  .pagination-wrapper {
    padding: 16px;
    text-align: center;
    border-top: 1px solid #f0f0f0;
    background: #fff;
  }

  .empty-state {
    padding: 40px 0;
    text-align: center;
  }
}

// 响应式设计
@media (max-width: 768px) {
  .artifact-version-list {
    .version-item {
      .version-main {
        .version-info {
          .version-meta {
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
          }
        }
      }
    }
  }
}
</style>
